<template>
  <div class="projects-panel">

    <!-- ── LIST ─────────────────────────────────────────────────────────── -->
    <div v-if="projectMode === 'list'" class="list-view">
      <div v-if="showSkeleton" class="cards-grid">
        <div v-for="i in 6" :key="i" class="project-card">
          <div class="card-photo"><SkeletonBlock width="100%" height="100%" radius="0" /></div>
          <div class="card-body">
            <SkeletonBlock width="70%" height="15px" />
            <SkeletonBlock width="50%" height="11px" />
            <SkeletonBlock width="40%" height="11px" />
          </div>
        </div>
      </div>
      <div v-else-if="projects.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
        </div>
        <p class="empty-title">Nenhum projeto cadastrado</p>
        <p class="empty-sub">Crie o primeiro projeto para começar.</p>
        <button v-if="!isAnalyst" class="btn-new" type="button" @click="$emit('edit-project', '')">
          + Novo projeto
        </button>
      </div>

      <div v-else>
        <div class="toolbar" v-if="!isAnalyst">
          <button class="btn-new" type="button" @click="$emit('edit-project', '')">+ Novo projeto</button>
        </div>

        <div class="cards-grid">
          <div
            v-for="proj in projects"
            :key="proj.id"
            class="project-card"
          >
            <!-- Photo -->
            <div class="card-photo" @click="$emit('view-project', proj.id)">
              <img
                v-if="proj.photoDataUrl"
                :src="$resolveUrl(proj.photoDataUrl)"
                :alt="proj.name"
                class="card-img"
              />
              <div v-else class="card-img-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
              </div>
            </div>

            <!-- Body -->
            <div class="card-body">
              <h3 class="card-title" @click="$emit('view-project', proj.id)">{{ proj.name }}</h3>
              <div class="card-meta">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                <span>{{ formatResponsibleNames(proj) }}</span>
              </div>
              <div class="card-meta">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                <span>{{ formatReportDate(proj.createdAt.slice(0, 10)) }}</span>
              </div>
            </div>

            <!-- Footer actions -->
            <div class="card-footer">
              <button class="card-btn" type="button" @click="$emit('view-project', proj.id)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                Visualizar
              </button>
              <template v-if="!isAnalyst">
                <button class="card-btn" type="button" @click="$emit('edit-project', proj.id)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Editar
                </button>
                <button class="card-btn card-btn--danger" type="button" @click="$emit('delete-project', proj)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  Excluir
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── VIEW ─────────────────────────────────────────────────────────── -->
    <div v-else-if="projectMode === 'view'" class="detail-view">
      <button class="back-btn" type="button" @click="$emit('close-project-mode')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        Voltar
      </button>
      <div v-if="selectedProject" class="detail-card">
        <div class="detail-photo-wrap">
          <img
            v-if="selectedProject.photoDataUrl"
            class="detail-photo"
            :src="$resolveUrl(selectedProject.photoDataUrl)"
            :alt="selectedProject.name"
          />
          <div v-else class="detail-photo detail-photo--empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
            <span>Sem foto</span>
          </div>
        </div>
        <div class="detail-info">
          <h2 class="detail-title">{{ selectedProject.name }}</h2>
          <div class="detail-row">
            <span class="detail-label">Acesso</span>
            <span class="detail-value">{{ selectedProject.accessMode === 'read' ? 'Somente leitura' : 'Edição' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Responsáveis</span>
            <span class="detail-value">{{ formatResponsibleNames(selectedProject) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Temporários</span>
            <span class="detail-value">{{ formatTempResponsibleNames(selectedProject) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Criado em</span>
            <span class="detail-value">{{ formatReportDate(selectedProject.createdAt.slice(0, 10)) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── FORM ─────────────────────────────────────────────────────────── -->
    <div v-else-if="!isAnalyst" class="form-view">
      <button class="back-btn" type="button" @click="$emit('close-project-mode')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        Voltar
      </button>

      <div class="form-card">
        <h2 class="form-title">{{ selectedProject ? 'Editar projeto' : 'Novo projeto' }}</h2>

        <div class="form-field">
          <label class="form-label" for="project-name">Nome do projeto</label>
          <input
            id="project-name"
            class="form-input"
            :value="projectForm.name"
            type="text"
            placeholder="Nome do projeto"
            @input="updateField('name', ($event.target as HTMLInputElement).value)"
          />
        </div>

        <div class="form-field">
          <label class="form-label">Foto do projeto</label>
          <div
            class="drop-zone"
            :class="{ 'drop-zone--over': isDragOver }"
            @dragenter.prevent="isDragOver = true"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="onPhotoDrop"
            @click="triggerProjectPhotoInput"
          >
            <input ref="projectPhotoInputRef" id="project-photo" class="hidden-input" type="file" accept="image/*" @change="onProjectPhotoSelected" />
            <template v-if="projectForm.photoDataUrl">
              <img class="drop-preview" :src="$resolveUrl(projectForm.photoDataUrl)" alt="preview" />
              <span class="drop-hint">Clique para trocar a foto</span>
            </template>
            <template v-else>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" class="drop-icon"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span class="drop-hint">Clique ou arraste uma imagem aqui</span>
            </template>
          </div>
        </div>

        <div class="form-field">
          <label class="form-label">Usuários responsáveis</label>
          <div class="responsible-list">
            <div
              v-for="(username, index) in projectForm.responsibleUsernames"
              :key="`resp-${index}`"
              class="responsible-row"
            >
              <select class="form-select" :value="username" @change="updateResponsible(index, ($event.target as HTMLSelectElement).value)">
                <option value="">Selecione um usuário</option>
                <option v-for="acc in accounts" :key="acc.username" :value="acc.username">
                  {{ acc.name }} ({{ acc.username }})
                </option>
              </select>
              <button
                class="btn-remove"
                type="button"
                :disabled="projectForm.responsibleUsernames.length <= 1"
                @click="$emit('remove-responsible', index)"
                title="Remover"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <button class="btn-add-responsible" type="button" @click="$emit('add-responsible')">
              + Adicionar responsável
            </button>
          </div>
        </div>

        <p v-if="projectError" class="form-error">{{ projectError }}</p>

        <div class="form-actions">
          <button class="btn-save" type="button" @click="$emit('save-project')">Salvar projeto</button>
          <button class="btn-cancel" type="button" @click="$emit('close-project-mode')">Cancelar</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue'
import { projectsStore, type Project } from '@/store/projects'
import type { Account } from '@/store/accounts'
import SkeletonBlock from '@/components/skeleton/SkeletonBlock.vue'

type ProjectFormFieldValue = string | boolean | string[]

export default defineComponent({
  name: 'ProjectsPanel',
  components: { SkeletonBlock },
  props: {
    projects: { type: Array as PropType<Project[]>, default: () => [] },
    projectMode: { type: String, required: true },
    selectedProject: { type: Object as PropType<Project | null>, default: null },
    projectForm: {
      type: Object as PropType<{ id: string; name: string; photoDataUrl: string; responsibleUsernames: string[] }>,
      required: true
    },
    projectError: { type: String, default: '' },
    accounts: { type: Array as PropType<Account[]>, default: () => [] },
    formatReportDate: { type: Function, required: true },
    isAnalyst: { type: Boolean, default: false }
  },
  emits: [
    'view-project',
    'edit-project',
    'delete-project',
    'close-project-mode',
    'save-project',
    'update-project-form',
    'add-responsible',
    'remove-responsible'
  ],
  setup(props, { emit }) {
    const projectPhotoInputRef = ref<HTMLInputElement | null>(null)
    const isDragOver = ref(false)
    const showSkeleton = computed(() => projectsStore.state.loading && props.projects.length === 0)

    const updateField = (field: string, value: ProjectFormFieldValue) => {
      emit('update-project-form', { field, value })
    }
    const updateResponsible = (index: number, value: string) => {
      emit('update-project-form', { field: 'responsibleUsernames', index, value })
    }
    const onProjectPhotoSelected = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          emit('update-project-form', { field: 'photoDataUrl', value: reader.result })
        }
      }
      reader.readAsDataURL(file)
      input.value = ''
    }
    const triggerProjectPhotoInput = () => projectPhotoInputRef.value?.click()
    const onPhotoDrop = (event: DragEvent) => {
      isDragOver.value = false
      const file = event.dataTransfer?.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          emit('update-project-form', { field: 'photoDataUrl', value: reader.result })
        }
      }
      reader.readAsDataURL(file)
    }
    const formatResponsibleNames = (project: Project) => {
      if (project.responsibleNames?.length) return project.responsibleNames.join(', ')
      if (project.ownerName) return project.ownerName
      return '-'
    }
    const formatTempResponsibleNames = (project: Project) => {
      const names = (project.tempResponsibles || []).map((item) => item.username).filter(Boolean)
      return names.length ? names.join(', ') : 'Nenhum'
    }
    return {
      projectPhotoInputRef,
      isDragOver,
      showSkeleton,
      updateField,
      updateResponsible,
      onProjectPhotoSelected,
      triggerProjectPhotoInput,
      onPhotoDrop,
      formatResponsibleNames,
      formatTempResponsibleNames
    }
  }
})
</script>

<style scoped lang="scss">
.projects-panel { height: 100%; }

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 8px;
  text-align: center;
  color: var(--ink-muted, #888);
}
.empty-icon { opacity: .35; margin-bottom: 4px; }
.empty-title { font-size: 18px; font-weight: 700; color: var(--ink, #2c2b27); margin: 0; }
.empty-sub { font-size: 13px; margin: 0 0 12px; }

/* ── Toolbar ── */
.toolbar { display: flex; justify-content: flex-end; margin-bottom: 20px; }

.btn-new {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  font-size: 13px; font-weight: 600;
  transition: opacity .15s;
  &:hover { opacity: .88; }
}

/* ── Cards grid ── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.project-card {
  border: 1px solid rgba(31, 30, 26, 0.1);
  border-radius: 14px;
  overflow: hidden;
  background: var(--card, #fff);
  display: flex;
  flex-direction: column;
  transition: box-shadow .18s ease, transform .18s ease;
  &:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,.09);
    transform: translateY(-2px);
  }
}

.card-photo {
  height: 160px;
  overflow: hidden;
  cursor: pointer;
  background: #f4f4f2;
  flex-shrink: 0;
}
.card-img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform .25s ease;
  .project-card:hover & { transform: scale(1.03); }
}
.card-img-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: rgba(31,30,26,.25);
}

.card-body {
  padding: 14px 16px 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink, #2c2b27);
  cursor: pointer;
  line-height: 1.3;
  &:hover { color: var(--system-navbar, #0f4c81); }
}
.card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ink-muted, #888);
  svg { flex-shrink: 0; opacity: .7; }
}

.card-footer {
  display: flex;
  gap: 2px;
  padding: 8px 10px;
  border-top: 1px solid rgba(31,30,26,.07);
  flex-wrap: wrap;
}
.card-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 10px; border-radius: 6px; border: none; cursor: pointer;
  background: transparent; color: var(--ink-muted, #666);
  font-size: 12px; font-weight: 500;
  transition: background .12s, color .12s;
  &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
  &--danger:hover { background: #fef2f2; color: #dc2626; }
}

/* ── Back button ── */
.back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: 7px; border: 1px solid rgba(31,30,26,.14);
  background: transparent; cursor: pointer;
  font-size: 13px; font-weight: 600; color: var(--ink-muted, #666);
  margin-bottom: 20px;
  transition: background .12s, color .12s;
  &:hover { background: var(--surface, rgba(0,0,0,.04)); color: var(--ink, #2c2b27); }
}

/* ── Detail view ── */
.detail-card {
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 14px;
  overflow: hidden;
  background: var(--card, #fff);
  max-width: 640px;
}
.detail-photo-wrap { height: 240px; overflow: hidden; background: #f4f4f2; }
.detail-photo {
  width: 100%; height: 100%; object-fit: cover;
}
.detail-photo--empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: rgba(31,30,26,.3); font-size: 13px;
}
.detail-info { padding: 20px 24px; display: flex; flex-direction: column; gap: 2px; }
.detail-title { margin: 0 0 12px; font-size: 22px; font-weight: 800; color: var(--ink, #2c2b27); }
.detail-row {
  display: flex; gap: 12px; padding: 10px 0;
  border-bottom: 1px solid rgba(31,30,26,.06);
  &:last-child { border-bottom: none; }
}
.detail-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); min-width: 110px; flex-shrink: 0; }
.detail-value { font-size: 13px; color: var(--ink, #2c2b27); }

/* ── Form view ── */
.form-card {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 14px;
  padding: 28px 28px 24px;
  max-width: 580px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.form-title { margin: 0; font-size: 20px; font-weight: 800; color: var(--ink, #2c2b27); }
.form-field { display: flex; flex-direction: column; gap: 6px; }
.form-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #666); text-transform: uppercase; letter-spacing: .04em; }
.form-input, .form-select {
  width: 100%; padding: 9px 12px; border-radius: 8px;
  border: 1.5px solid rgba(31,30,26,.18);
  background: #fff; font-size: 14px; color: var(--ink, #2c2b27);
  outline: none; box-sizing: border-box;
  transition: border-color .15s;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}

.drop-zone {
  border: 2px dashed rgba(31,30,26,.2);
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  background: #fafafa;
  transition: border-color .18s, background .18s;
  min-height: 120px;
  &--over, &:hover { border-color: var(--system-navbar, #0f4c81); background: #f0f5ff; }
}
.drop-icon { color: var(--ink-muted, #aaa); }
.drop-hint { font-size: 13px; color: var(--ink-muted, #888); }
.drop-preview { width: 120px; height: 80px; object-fit: cover; border-radius: 6px; }
.hidden-input { display: none; }

.responsible-list { display: flex; flex-direction: column; gap: 8px; }
.responsible-row { display: flex; gap: 8px; align-items: center; }
.responsible-row .form-select { flex: 1; }
.btn-remove {
  width: 32px; height: 32px; flex-shrink: 0; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.15);
  background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--ink-muted, #888);
  transition: border-color .12s, color .12s, background .12s;
  &:hover:not(:disabled) { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
  &:disabled { opacity: .35; cursor: default; }
}
.btn-add-responsible {
  align-self: flex-start; padding: 7px 14px; border-radius: 7px; border: 1.5px dashed rgba(31,30,26,.2);
  background: transparent; cursor: pointer; font-size: 13px; color: var(--ink-muted, #666);
  transition: border-color .12s, color .12s;
  &:hover { border-color: var(--system-navbar, #0f4c81); color: var(--system-navbar, #0f4c81); }
}

.form-error { margin: 0; font-size: 13px; color: #dc2626; }

.form-actions { display: flex; gap: 10px; }
.btn-save {
  padding: 10px 22px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  font-size: 14px; font-weight: 700;
  transition: opacity .15s;
  &:hover { opacity: .88; }
}
.btn-cancel {
  padding: 10px 18px; border-radius: 8px; cursor: pointer;
  background: transparent; border: 1.5px solid rgba(31,30,26,.18);
  font-size: 14px; font-weight: 600; color: var(--ink-muted, #666);
  transition: background .12s;
  &:hover { background: var(--surface, rgba(0,0,0,.04)); }
}

@media (max-width: 600px) {
  .cards-grid { grid-template-columns: 1fr; }
  .form-card { padding: 20px 16px; }
  .detail-info { padding: 16px; }
  .detail-photo-wrap { height: 180px; }
}
</style>
